import React, { useRef, useState, useEffect } from 'react';
import { format, parse } from 'date-fns';
import ComponentHeader from '../ComponentHeader';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';

import StyledWrapper from '../style';
import { getElementIcon } from '../../../FormBuilder/utils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Tooltip from '@mui/material/Tooltip';

function updateFormat(props, oldFormatMask) {
	const { showTimeSelect, showTimeSelectOnly, showTimeInput } = props.data;
	const dateFormat = showTimeSelect && showTimeSelectOnly ? '' : props.data.dateFormat;
	const timeFormat = showTimeSelect || showTimeInput ? props.data.timeFormat : '';
	const formatMask = `${dateFormat} ${timeFormat}`.trim();
	const updated = formatMask !== oldFormatMask;
	return { updated, formatMask };
}

function updateDateTime(props, state, formatMask) {
	let value;
	let internalValue;
	const { defaultToday } = props.data;
	if (defaultToday && (props.defaultValue === '' || props.defaultValue === undefined)) {
		value = format(new Date(), formatMask);
		internalValue = new Date();
	} else {
		value = props.defaultValue;
		if (value === '' || value === undefined) {
			internalValue = undefined;
		} else {
			internalValue = parse(value, state.formatMask, new Date());
		}
	}
	return {
		value,
		internalValue,
		placeholder: formatMask.toLowerCase(),
		defaultToday,
		formatMask: state.formatMask
	};
}

const DatePicker = (props) => {
	const inputField = useRef(null);
	const labelStartRef = useRef(null);
	const labelEndRef = useRef(null);

	const { formatMask } = updateFormat(props, null);
	const [state, setState] = useState(() => ({
		...updateDateTime(props, { formatMask }, formatMask),
		isStartOverflowing: false,
		isEndOverflowing: false
	}));

	// Sync state with props
	useEffect(() => {
		const { updated, formatMask: newFormatMask } = updateFormat(props, state.formatMask);
		if (props.data.defaultToday !== state.defaultToday || updated) {
			setState((prev) => ({
				...prev,
				...updateDateTime(props, state, newFormatMask)
			}));
		}
		// eslint-disable-next-line
	}, [props.data.defaultToday, props.data.dateFormat, props.data.timeFormat]);

	// Overflow checks
	useEffect(() => {
		if (labelStartRef.current && labelStartRef.current.scrollWidth) {
			setState((prev) => ({
				...prev,
				isStartOverflowing: labelStartRef.current.scrollWidth > labelStartRef.current.clientWidth
			}));
		}
		if (labelEndRef.current && labelEndRef.current.scrollWidth) {
			setState((prev) => ({
				...prev,
				isEndOverflowing: labelEndRef.current.scrollWidth > labelEndRef.current.clientWidth
			}));
		}
	}, []);

	const handleChange = (dt) => {
		let placeholder;
		const { formatMask } = state;
		if (dt && dt.target) {
			placeholder = dt && dt.target && dt.target.value === '' ? formatMask.toLowerCase() : '';
			const formattedDate = dt.target.value ? format(dt.target.value, formatMask) : '';
			setState((prev) => ({
				...prev,
				value: formattedDate,
				internalValue: formattedDate,
				placeholder
			}));
		} else {
			setState((prev) => ({
				...prev,
				value: dt ? format(dt, formatMask) : '',
				internalValue: dt,
				placeholder
			}));
		}
	};

	const inputProps = {
		type: 'date',
		className: 'form-control',
		name: props.data.field_name
	};
	if (props.mutable) {
		inputProps.defaultValue = props.defaultValue;
		inputProps.ref = inputField;
	}

	let baseClasses = 'SortableItem rfb-item';
	if (props.data.pageBreakBefore) {
		baseClasses += ' alwaysbreak';
	}

	const label =
		props?.data?.label && props?.data?.label !== ' ' ? props?.data?.label : props?.data?.text;
	const isFormBuilder = true;

	return (
		<div style={{ ...props.style }} className={baseClasses}>
			<LocalizationProvider dateAdapter={AdapterDayjs} style={{ width: '100%' }}>
				<ComponentHeader {...props} />
				<div
					className="form-group"
					style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}
				>
					<div style={{ display: 'flex', gap: '10px' }}>
						<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
							<>
								<Tooltip
									arrow
									placement="top"
									title={
										state.isStartOverflowing
											? `${label ? label + ' ' : ''}Start Date ${
													isFormBuilder && props.data?.unique_identifier
														? `(ff-${props.data?.unique_identifier})`
														: `(tmp-${props.data?.temp_order})`
											  }`
											: ''
									}
								>
									<StyledWrapper.CustomLabel disabled={props?.disabled}>
										<StyledWrapper.LabelContents ref={labelStartRef}>
											{isFormBuilder && getElementIcon(props.data?.key)}
											{`${label ? label + ' ' : ''}Start Date`}
											{isFormBuilder && (
												<span>
													{props.data?.unique_identifier
														? `(ff-${props.data?.unique_identifier})`
														: `(tmp-${props.data?.temp_order})`}
												</span>
											)}
										</StyledWrapper.LabelContents>
										{props?.data?.required && (
											<StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>
										)}
									</StyledWrapper.CustomLabel>
								</Tooltip>
							</>
							<StyledWrapper.CustomDatePicker
								minDate={props?.data?.min_date}
								maxDate={props?.data?.max_date}
								disabled={props?.disabled}
								renderInput={(params) => (
									<TextField
										disabled={props?.disabled}
										InputLabelProps={{ shrink: isFormBuilder || undefined }}
										required={props?.data?.required}
										data-testid={`datepicker-textfield`}
										style={{ width: '100%' }}
										{...params}
									/>
								)}
							/>
						</StyledWrapper.FormControlNoBorder>
						<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
							<>
								<Tooltip
									arrow
									placement="top"
									title={
										state.isEndOverflowing
											? `${label ? label + ' ' : ''}End Date ${
													isFormBuilder && props.data?.unique_identifier
														? `(ff-${props.data?.unique_identifier})`
														: `(tmp-${props.data?.temp_order})`
											  }`
											: ''
									}
								>
									<StyledWrapper.CustomLabel disabled={props?.disabled}>
										<StyledWrapper.LabelContents ref={labelEndRef}>
											{isFormBuilder && getElementIcon(props.data?.key)}
											{`${label ? label + ' ' : ''}End Date`}
											{isFormBuilder && (
												<span>
													{props.data?.unique_identifier
														? `(ff-${props.data?.unique_identifier})`
														: `(tmp-${props.data?.temp_order})`}
												</span>
											)}
										</StyledWrapper.LabelContents>
										{props?.data?.required && (
											<StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>
										)}
									</StyledWrapper.CustomLabel>
								</Tooltip>
							</>
							<StyledWrapper.CustomDatePicker
								minDate={props?.data?.min_date}
								maxDate={props?.data?.max_date}
								disabled={props?.disabled}
								renderInput={(params) => (
									<TextField
										disabled={props?.disabled}
										InputLabelProps={{ shrink: isFormBuilder || undefined }}
										required={props?.data?.required}
										data-testid={`datepicker-textfield`}
										style={{ width: '100%' }}
										{...params}
									/>
								)}
							/>
						</StyledWrapper.FormControlNoBorder>
					</div>
					{!props?.data?.custom_options?.includes('hideTimePeriod') && (
						<div style={{ display: 'flex', gap: '10px' }}>
							<TextField
								id={`${props?.data?.name}-totalTime`}
								value={'(not defined)'}
								label={'Total Time'}
								sx={{ width: '100%' }}
								disabled
								InputLabelProps={{ shrink: isFormBuilder || undefined }}
							/>
							<FormControl sx={{ width: '100%' }} variant="outlined">
								<StyledWrapper.CustomLabel
									value={'(not defined)'}
									disabled
									htmlFor={`${props?.data?.name}-timeRemaining`}
									shrink={isFormBuilder || undefined}
								>
									<StyledWrapper.LabelContents>
										{isFormBuilder ? 'Time Remaining' : ''}
									</StyledWrapper.LabelContents>
								</StyledWrapper.CustomLabel>
								<StyledWrapper.OutlinedInputReadOnly
									type={'text'}
									disabled={true}
									style={{ width: '100%' }}
									inputComponent={() => {
										return (
											<>
												<TextField
													error={props?.data?.error}
													InputLabelProps={{ shrink: isFormBuilder || undefined }}
													id={`${props?.data?.name}-timeRemaining`}
													sx={{ width: 'fit-content' }}
													value={'(not defined)'}
													disabled
												/>
											</>
										);
									}}
								/>
							</FormControl>
						</div>
					)}
				</div>
			</LocalizationProvider>
		</div>
	);
};

export default DatePicker;
